import React from 'react';
import { Box, Typography } from '@mui/material';

interface ChalkboardSpecialsProps {
  content: string;
  timestamp?: Date;
}

/**
 * ChalkboardSpecials - A styled chalkboard component for displaying daily specials
 * Renders specials text in a visually appealing chalkboard-style card
 */
const ChalkboardSpecials: React.FC<ChalkboardSpecialsProps> = ({ content, timestamp }) => {
  // Parse the content - remove the intro text and extract just the specials
  // Content format: "Just wanted to let you know...\n\n• Item 1\n• Item 2"
  const parseSpecials = (text: string): string[] => {
    // Split by newlines and filter out empty lines
    const lines = text.split('\n').filter(line => line.trim());
    
    // Find where the bullet points start (skip intro text)
    const bulletStartIndex = lines.findIndex(line => line.trim().startsWith('•'));
    
    if (bulletStartIndex >= 0) {
      // Return just the bullet items, removing the bullet character
      return lines.slice(bulletStartIndex).map(line => 
        line.trim().replace(/^•\s*/, '')
      );
    }
    
    // If no bullets found, return all lines (fallback)
    return lines;
  };

  const specialItems = parseSpecials(content);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        my: 2,
        px: 1
      }}
    >
      <Box
        sx={{
          // Chalkboard styling
          backgroundColor: '#2b2b2b',
          border: '12px solid #4a3728', // Wooden frame
          borderRadius: '4px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.4), inset 0 0 50px rgba(255,255,255,0.02)',
          color: '#fdfdfd',
          padding: { xs: '20px 16px', sm: '28px 24px' },
          width: '100%',
          maxWidth: '340px',
          position: 'relative',
          // Subtle chalk dust texture effect
          backgroundImage: 'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.03) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.02) 0%, transparent 40%)',
          // Slight imperfection for realism
          transform: 'rotate(-0.3deg)',
        }}
      >
        {/* Title */}
        <Typography
          sx={{
            fontFamily: '"Amatic SC", "Brush Script MT", cursive',
            fontSize: { xs: '1.8rem', sm: '2.2rem' },
            fontWeight: 700,
            textAlign: 'center',
            marginBottom: '16px',
            textDecoration: 'underline',
            textDecorationThickness: '2px',
            textUnderlineOffset: '4px',
            textTransform: 'uppercase',
            letterSpacing: '2px',
            // Chalk effect
            textShadow: '1px 1px 2px rgba(255,255,255,0.1)',
          }}
        >
          Today's Specials
        </Typography>

        {/* Menu Items */}
        <Box sx={{ mt: 2 }}>
          {specialItems.map((item, index) => (
            <Typography
              key={index}
              sx={{
                fontFamily: '"Gochi Hand", "Comic Sans MS", cursive',
                fontSize: { xs: '1.1rem', sm: '1.2rem' },
                marginBottom: '12px',
                lineHeight: 1.4,
                paddingLeft: '8px',
                // Bullet point styling
                '&::before': {
                  content: '"•"',
                  marginRight: '8px',
                  color: '#e0e0e0',
                },
                // Slight chalk imperfection
                opacity: 0.95,
              }}
            >
              {item}
            </Typography>
          ))}
        </Box>

        {/* Subtle chalk dust decoration */}
        <Box
          sx={{
            position: 'absolute',
            bottom: '8px',
            right: '12px',
            width: '30px',
            height: '3px',
            backgroundColor: 'rgba(255,255,255,0.15)',
            borderRadius: '2px',
            transform: 'rotate(-5deg)',
          }}
        />
      </Box>
    </Box>
  );
};

export default ChalkboardSpecials;
